import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Loader2, CheckSquare, Clock, AlertCircle, User, Calendar, Filter,
  Search, ListTodo, Inbox, ChevronRight,
} from 'lucide-react';

const STATUS_LABELS = {
  todo: { label: 'À faire', color: 'bg-slate-100 text-slate-700' },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  done: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700' },
  archived: { label: 'Archivée', color: 'bg-slate-50 text-slate-400' },
};
const PRIORITY_LABELS = {
  low: { label: 'Basse', color: 'border-slate-200 text-slate-500' },
  medium: { label: 'Moyenne', color: 'border-blue-200 text-blue-700' },
  high: { label: 'Haute', color: 'border-amber-300 text-amber-700' },
  urgent: { label: 'Urgente', color: 'border-red-300 text-red-700' },
};

export default function Tasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', assignee: '', mine: false });
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium', due_date: '', assignee_user_id: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => { document.title = 'Tâches - Reno Resolve'; load(); loadUsers(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.assignee) params.assignee_user_id = filter.assignee;
      if (filter.mine) params.mine = true;
      const r = await api.get('/tasks', { params });
      setTasks(r.data?.tasks || []);
    } catch (e) {
      toast.error("Impossible de charger les tâches");
    }
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const r = await api.get('/users/list-slim');
      setUsers(r.data?.users || []);
    } catch (_) {}
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    setCreating(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description || null,
        priority: form.priority,
        due_date: form.due_date || null,
        assignee_user_id: form.assignee_user_id || null,
      };
      const r = await api.post('/tasks', body);
      toast.success('Tâche créée');
      setShowCreate(false);
      setForm({ title: '', description: '', priority: 'medium', due_date: '', assignee_user_id: '' });
      navigate(`/taches/${r.data.task_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    }
    setCreating(false);
  };

  const filtered = search.trim()
    ? tasks.filter((t) =>
        (t.title || '').toLowerCase().includes(search.toLowerCase())
        || (t.description || '').toLowerCase().includes(search.toLowerCase())
        || (t.entity_label || '').toLowerCase().includes(search.toLowerCase())
      )
    : tasks;

  const userName = (uid) => {
    if (!uid) return null;
    const u = users.find((x) => x.user_id === uid);
    return u?.name || u?.email || uid;
  };

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Tâches
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Gestion collaborative des tâches d'équipe — assignation, commentaires, pièces jointes.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="task-create-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvelle tâche
        </Button>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-3 flex-wrap" data-testid="tasks-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            className="pl-9"
            data-testid="tasks-search"
          />
        </div>
        <select
          value={filter.status}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
          className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
          data-testid="filter-status"
        >
          <option value="">Tous statuts</option>
          <option value="todo">À faire</option>
          <option value="in_progress">En cours</option>
          <option value="done">Terminé</option>
          <option value="archived">Archivée</option>
        </select>
        <select
          value={filter.assignee}
          onChange={(e) => setFilter((f) => ({ ...f, assignee: e.target.value }))}
          className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
          data-testid="filter-assignee"
        >
          <option value="">Tous assignés</option>
          {users.map((u) => (
            <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
          ))}
        </select>
        <Button
          variant={filter.mine ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter((f) => ({ ...f, mine: !f.mine }))}
          data-testid="filter-mine"
        >
          <Inbox className="w-3.5 h-3.5 mr-1" /> Mes tâches
        </Button>
      </div>

      {/* Liste */}
      {loading && (
        <div className="p-12 text-center text-slate-500" key="tasks-loading">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" key="tasks-empty">
          <ListTodo className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">Aucune tâche pour le moment.</p>
          <Button onClick={() => setShowCreate(true)} variant="outline" className="mt-4" data-testid="task-create-empty">
            <Plus className="w-4 h-4 mr-1.5" /> Créer la première
          </Button>
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="tasks-list">
          {filtered.map((t) => {
            const st = STATUS_LABELS[t.status] || STATUS_LABELS.todo;
            const pr = PRIORITY_LABELS[t.priority] || PRIORITY_LABELS.medium;
            return (
              <button
                key={t.task_id}
                type="button"
                onClick={() => navigate(`/taches/${t.task_id}`)}
                className="w-full p-4 text-left hover:bg-slate-50 transition flex items-center gap-4"
                data-testid={`task-row-${t.task_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900 truncate">{t.title}</span>
                    <Badge className={st.color + ' text-xs'}>{st.label}</Badge>
                    <Badge variant="outline" className={pr.color + ' text-xs'}>{pr.label}</Badge>
                    {t.entity_label && (
                      <Badge variant="outline" className="text-xs border-slate-200 text-slate-600">
                        {t.entity_type} · {t.entity_label}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
                    {t.assignee_user_id && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" /> {userName(t.assignee_user_id)}
                      </span>
                    )}
                    {t.due_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {t.due_date}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> par {t.created_by?.name || t.created_by?.email}
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {/* Modal création */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="task-create-modal"
          >
            <h3 className="text-lg font-semibold">Nouvelle tâche</h3>
            <Input
              placeholder="Titre *"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="task-form-title"
              autoFocus
            />
            <textarea
              placeholder="Description (facultatif)"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="task-form-description"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Priorité</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                  className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="task-form-priority"
                >
                  <option value="low">Basse</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Haute</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Échéance</label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                  data-testid="task-form-due"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Assigner à</label>
                <select
                  value={form.assignee_user_id}
                  onChange={(e) => setForm((f) => ({ ...f, assignee_user_id: e.target.value }))}
                  className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="task-form-assignee"
                >
                  <option value="">— Personne —</option>
                  {users.map((u) => (
                    <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>Annuler</Button>
              <Button onClick={handleCreate} disabled={creating} data-testid="task-form-submit">
                {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                Créer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
